import { LngLat, Map as MlMap } from "maplibre-gl";
import { tourPerformance } from "./webgl-maplibre.tour-performance";

export function routeTourBearing(from: LngLat, to: LngLat): number {
  const radians = Math.PI / 180;
  const delta = (to.lng - from.lng) * radians;
  const a = from.lat * radians,
    b = to.lat * radians;
  return (
    Math.atan2(Math.sin(delta) * Math.cos(b), Math.cos(a) * Math.sin(b) - Math.sin(a) * Math.cos(b) * Math.cos(delta)) /
    radians
  );
}

/** Approximately constant screen speed: each zoom-out doubles ground speed. */
export function routeTourSpeed(zoom: number): number {
  return 3 * Math.max(5, Math.min(20000, 150 * Math.pow(2, 14 - zoom)));
}

export function routeTerrainSamples(points: number[][]): number[][] {
  const lengths = points
    .slice(1)
    .map((p, i) => new LngLat(points[i][0], points[i][1]).distanceTo(new LngLat(p[0], p[1])));
  const total = lengths.reduce((a, b) => a + b, 0);
  const samples = points.map((p) => [p[0], p[1]]);
  if (total < 1) return samples;
  // Coarse one-time profile, including every waypoint and 64 samples along legs.
  for (let s = 1; s < 64; s++) {
    let distance = (total * s) / 64,
      leg = 0;
    while (leg < lengths.length - 1 && distance > lengths[leg]) distance -= lengths[leg++];
    const fraction = lengths[leg] ? distance / lengths[leg] : 0;
    const a = points[leg],
      b = points[leg + 1];
    const dx = ((b[0] - a[0] + 540) % 360) - 180;
    samples.push([((a[0] + dx * fraction + 540) % 360) - 180, a[1] + (b[1] - a[1]) * fraction]);
  }
  return samples;
}

/** A camera-only tour with independent progress, so zoom cannot skip a route leg. */
export class RouteTour {
  private finish?: () => void;
  private movementEnd?: () => void;
  private resumeMovement?: () => void;
  private previousMaxPitch = 60;
  private renderFrame = (): void => tourPerformance.frame(performance.now());
  private pointers = new Map<number, { x: number; y: number }>();
  private pinchDistance = 0;
  private pinching = false;
  private scrollEnabled = false;
  private touchEnabled = false;
  private dragEnabled = false;
  private rotateEnabled = false;
  private centerClamped = true;
  constructor(private map: MlMap) {}

  get isPlaying(): boolean {
    return !!this.finish;
  }

  play(
    points: number[][],
    onArrival?: () => void,
    referenceElevationM = this.map.getCenterElevation(),
    destinationElevationM = 0
  ): Promise<void> {
    this.stop();
    const route = points
      .map((p) => new LngLat(p[0], p[1]))
      .filter((p, i, all) => i === 0 || all[i - 1].distanceTo(p) > 1);
    if (route.length < 2) return Promise.resolve();
    tourPerformance.start();
    this.centerClamped = this.map.getCenterClampedToGround();
    this.map.setCenterClampedToGround(false);
    this.previousMaxPitch = this.map.getMaxPitch();
    this.map.setMaxPitch(Math.max(30, this.previousMaxPitch));
    this.map.jumpTo({
      center: route[0],
      bearing: routeTourBearing(route[0], route[1]),
      pitch: 30,
      elevation: referenceElevationM,
    });
    const canvas = this.map.getCanvas();
    this.scrollEnabled = this.map.scrollZoom.isEnabled();
    this.touchEnabled = this.map.touchZoomRotate.isEnabled();
    this.dragEnabled = this.map.dragPan.isEnabled();
    this.rotateEnabled = this.map.dragRotate.isEnabled();
    this.map.scrollZoom.disable();
    this.map.touchZoomRotate.disable();
    this.map.dragPan.disable();
    this.map.dragRotate.disable();
    canvas.addEventListener("wheel", this.wheel, { passive: false });
    canvas.addEventListener("pointerdown", this.pointerDown);
    canvas.addEventListener("pointermove", this.pointerMove);
    canvas.addEventListener("pointerup", this.pointerUp);
    canvas.addEventListener("pointercancel", this.pointerUp);
    this.map.on("remove", this.stop);
    this.map.on("render", this.renderFrame);
    return new Promise((resolve) => {
      this.finish = resolve;
      let index = 1;
      const destination = route[route.length - 1];
      const orbit = () => {
        if (!this.finish) return;
        this.resumeMovement = orbit;
        // Native linear rotation at 8 degrees/second; no per-frame camera resets.
        this.animate({ bearing: this.map.getBearing() + 120, duration: 15000 }, orbit);
      };
      const arrive = () => {
        // Flat map features lie at zero elevation, even when the tour overlays
        // use real terrain heights. Aim at the rendered surface on arrival.
        this.map.setCenterElevation(this.map.getTerrain() ? destinationElevationM : 0);
        this.resumeMovement = orbit; // A manual zoom cancels the automatic arrival zoom.
        onArrival?.();
        if (!this.finish) return;
        this.animate(
          {
            center: destination,
            zoom: Math.min(15, this.map.getMaxZoom()),
            pitch: 30,
            bearing: this.map.getBearing() + 16,
            duration: 2000,
          },
          orbit
        );
      };
      const travel = () => {
        if (!this.finish) return;
        // Restart only after a zoom gesture or a leg boundary, from the current
        // camera position. Speed is metres/second, independent of frame rate.
        const from = this.map.getCenter();
        while (index < route.length && from.distanceTo(route[index]) <= 1) index++;
        if (index >= route.length) {
          arrive();
          return;
        }
        const to = route[index];
        this.resumeMovement = travel;
        this.animate(
          {
            center: to,
            bearing: routeTourBearing(from, to),
            pitch: 30,
            duration: (1000 * from.distanceTo(to)) / routeTourSpeed(this.map.getZoom()),
          },
          () => {
            index++;
            if (index >= route.length) arrive();
            else travel();
          }
        );
      };
      travel();
    });
  }

  private animate(options: Parameters<MlMap["easeTo"]>[0], done: () => void): void {
    this.interruptMovement();
    this.movementEnd = () => {
      if (this.movementEnd) this.map.off("moveend", this.movementEnd);
      this.movementEnd = undefined;
      if (this.finish) done();
    };
    this.map.on("moveend", this.movementEnd);
    this.map.easeTo({ ...options, easing: (t) => t, freezeElevation: true, essential: true });
  }

  private interruptMovement(): void {
    if (this.movementEnd) this.map.off("moveend", this.movementEnd);
    this.movementEnd = undefined;
    this.map.stop();
  }

  private zoomBy(delta: number): void {
    this.zoomTo(this.map.getZoom() + delta);
  }

  /** Zoom without ending the tour: a bare setZoom interrupts the running leg,
   *  and its moveend then advances the tour to the next leg or the arrival. */
  zoomTo(zoom: number): void {
    const resume = this.resumeMovement;
    this.interruptMovement();
    this.map.setZoom(Math.max(this.map.getMinZoom(), Math.min(this.map.getMaxZoom(), zoom)));
    if (this.finish) resume?.();
  }

  private wheel = (event: WheelEvent): void => {
    event.preventDefault();
    const pixels = event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? 400 : 1);
    this.zoomBy(-pixels / 400);
  };

  private pointerDown = (event: PointerEvent): void => {
    if (event.button !== 0) return;
    this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    this.map.getCanvas().setPointerCapture(event.pointerId);
    if (this.pointers.size === 2) {
      this.pinching = true;
      this.pinchDistance = this.pointerDistance();
    }
  };

  private pointerDistance(): number {
    const [a, b] = [...this.pointers.values()];
    return a && b ? Math.hypot(a.x - b.x, a.y - b.y) : 0;
  }

  private pointerMove = (event: PointerEvent): void => {
    const previous = this.pointers.get(event.pointerId);
    if (!previous) return;
    if (!this.pinching && Math.hypot(event.clientX - previous.x, event.clientY - previous.y) > 10) {
      this.stop();
      return;
    }
    if (this.pinching) {
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      const distance = this.pointerDistance();
      if (distance > 0 && this.pinchDistance > 0) this.zoomBy(Math.log2(distance / this.pinchDistance));
      this.pinchDistance = distance;
    }
  };

  private pointerUp = (event: PointerEvent): void => {
    const tracked = this.pointers.delete(event.pointerId);
    if (tracked && !this.pinching) this.stop();
    if (this.pointers.size === 0) {
      this.pinching = false;
      this.pinchDistance = 0;
    }
  };

  stop = (): void => {
    if (!this.finish) return;
    const resolve = this.finish;
    this.finish = undefined;
    tourPerformance.stop();
    this.resumeMovement = undefined;
    this.interruptMovement();
    this.map.off("render", this.renderFrame);
    const canvas = this.map.getCanvas();
    canvas.removeEventListener("wheel", this.wheel);
    canvas.removeEventListener("pointerdown", this.pointerDown);
    canvas.removeEventListener("pointermove", this.pointerMove);
    canvas.removeEventListener("pointerup", this.pointerUp);
    canvas.removeEventListener("pointercancel", this.pointerUp);
    for (const id of this.pointers.keys()) if (canvas.hasPointerCapture(id)) canvas.releasePointerCapture(id);
    this.pointers.clear();
    this.pinching = false;
    this.pinchDistance = 0;
    if (this.scrollEnabled) this.map.scrollZoom.enable();
    if (this.touchEnabled) this.map.touchZoomRotate.enable();
    if (this.dragEnabled) this.map.dragPan.enable();
    if (this.rotateEnabled) this.map.dragRotate.enable();
    this.map.off("remove", this.stop);
    this.map.stop();
    this.map.setCenterClampedToGround(this.centerClamped);
    this.map.setMaxPitch(this.previousMaxPitch);
    resolve();
  };
}
